import classNames from "classnames";
import {
  ComponentPropsWithoutRef,
  useEffect,
  forwardRef,
  ForwardedRef,
  useRef,
  useCallback,
} from "react";
import useAsync from "../../../global_functions/useAsync";
import Alerts, { AlertEnum } from "../../base/dialogs/Alerts/Alerts";
import Button, { ButtonProps } from "../../base/buttons/Button/Button";
import Icon from "../../base/icons/Icon/Icon";
import { parseHtmlErrorMessage } from "@src/global_functions/backpackSdk/errors";
import { useMergedRef } from "@src/global_functions/hooks";

type AsyncButtonProps = ButtonProps<"button"> & {
  readonly onClick: (e: React.MouseEvent<HTMLElement>) => Promise<void>;
  readonly successMessage?: React.ReactNode;
  readonly errorMessageIcon?: boolean;
  readonly alertRenderLocation?:
    | "topRight"
    | "bottomRight"
    | "left"
    | "topLeft";
  readonly containerProps?: ComponentPropsWithoutRef<"div">;
  readonly alertWidth?: number;
};

const ALERT_MAX_WIDTH = 260;
function AsyncButton(
  {
    onClick,
    successMessage,
    containerProps,
    errorMessageIcon,
    alertRenderLocation: alertRenderLocationProp,
    alertWidth,
    ...props
  }: AsyncButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const [state, action] = useAsync(
    async (e: React.MouseEvent<HTMLElement>) => await onClick(e),
    [onClick]
  );

  const internalRef = useRef<HTMLButtonElement>();
  const ref = useMergedRef(internalRef, forwardedRef);

  // measure the distance to right side of the window to determine where to render the alert
  const { left } = internalRef?.current?.getBoundingClientRect() ?? {};
  const exceedsAlertWidth = window.innerWidth - (left ?? 0) < ALERT_MAX_WIDTH;
  const alertRenderLocation: Exclude<
    AsyncButtonProps["alertRenderLocation"],
    undefined
  > = alertRenderLocationProp ?? (exceedsAlertWidth ? "topLeft" : "topRight");

  // Add listener to remove success message on document click
  const { success, setState } = state;
  useEffect(() => {
    if (success) {
      const listener = () => {
        setState((prev) => ({ ...prev, success: false }));
      };
      document.addEventListener("click", listener);
      return () => document.removeEventListener("click", listener);
    }
  }, [success, setState]);

  const alertClassNames = classNames("absolute z-10", {
    "top-[calc(100%+2px)]": alertRenderLocation === "bottomRight",
    "bottom-[calc(100%+2px)]":
      alertRenderLocation === "topRight" || alertRenderLocation === "topLeft",
    "right-0 min-w-full": alertRenderLocation === "topLeft",
    "right-[calc(100%+8px)] top-0 ": alertRenderLocation === "left",
  });

  const errorMessage =
    !!state.error &&
    parseHtmlErrorMessage(
      typeof state.error === "string" ? state.error : state.error.message
    );

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, [setState]);

  return (
    <div className="relative" {...containerProps}>
      <Button
        {...props}
        ref={ref}
        loading={state.loading}
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          !state.loading && !props.disabled && action(e)
        }
      />
      {errorMessage && (
        <div className={alertClassNames}>
          <Alerts
            onClose={clearError}
            message={
              <span
                className={classNames("flex items-center gap-2", {
                  "min-w-[96px]":
                    errorMessage.split(" ").length > 5 && !alertWidth,
                })}
                style={{ width: alertWidth }}
              >
                {errorMessageIcon ? (
                  <Icon
                    name="warning"
                    className="shrink-0"
                    size="small"
                    color="negative"
                  />
                ) : null}
                {errorMessage}
              </span>
            }
            status={AlertEnum.Error}
          />{" "}
        </div>
      )}
      {state.success && successMessage && (
        <div className={alertClassNames}>
          <Alerts status={AlertEnum.Success} message={successMessage} />
        </div>
      )}
    </div>
  );
}

export default forwardRef(AsyncButton);
